import { isPlatformBrowser, SlicePipe } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, HostBinding, inject, input, OnDestroy, PLATFORM_ID, Signal, signal, ViewChild, } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBookmark as faBookmarkRegular, faCalendarDays } from '@fortawesome/free-regular-svg-icons';
import { faBookmark as faBookmarkSolid, faChevronDown, faChevronUp, faExternalLinkAlt, faLaptopCode, faPenToSquare, faShare } from '@fortawesome/free-solid-svg-icons';
import { filter, switchMap, tap } from 'rxjs';
import { BookmarkService } from 'src/app/@domain/bookmark/application/bookmark.service';
import { VideoService } from 'src/app/@domain/video/application/video.service';
import { AppRoute } from 'src/app/routing/types/app-route';
import { ImageDirective } from '../../../@common/DOM/image.directive';
import { Licences } from '../../../@common/licence/licence.configuration';
import { ToDatePipe } from '../../../@common/pipe/to-date.pipe';
import { CopyOnClickDirective } from '../../../@common/util/copy-on-click.directive';
import { toNumber } from '../../../@common/util/to-number';
import { NotificationService } from '../../../@domain/notification/application/notification.service';
import { MetaService } from '../../../@domain/seo/meta.service';
import { UserService } from '../../../@domain/user/application/user.service';
import { Subtitle, Video, VideoPart } from '../../../@generated';
import { CardGridComponent } from '../../../@ui/app/content/card-grid/card-grid.component';
import { RelatedCategoriesComponent } from '../../../@ui/app/content/related-categories/related-categories.component';
import { SlideItem, SlideItemComponent } from '../../../@ui/app/content/sections/slide-item/slide-item.component';
import { VideoItemComponent } from '../../../@ui/app/content/sections/video-item/video-item.component';
import { PlaylistDropdownComponent } from '../../../@ui/app/global/playlist-dropdown/playlist-dropdown.component';
import { SpinnerComponent } from '../../../@ui/app/global/spinner/spinner.component';
import { VideoPlayerComponent } from '../../../@ui/app/global/video-player/video-player.component';
import { AdminRoute } from '../../../routing/types/admin-route';

@Component({
    selector: 'app-video-page',
    templateUrl: './video.page.html',
    styleUrls: [ './video.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        VideoPlayerComponent,
        FaIconComponent,
        RouterLink,
        PlaylistDropdownComponent,
        CopyOnClickDirective,
        RelatedCategoriesComponent,
        SlideItemComponent,
        SpinnerComponent,
        SlicePipe,
        VideoItemComponent,
        CardGridComponent,
        ToDatePipe,
        ImageDirective
    ]
})
export default class VideoPage implements OnDestroy {
    private videoService = inject(VideoService);
    private userService = inject(UserService);
    private bookmarksService = inject(BookmarkService);
    private metaService = inject(MetaService);
    private platformId = inject(PLATFORM_ID);
    private notificationService = inject(NotificationService);

    @ViewChild('video', { static: false }) videoEl!: ElementRef;
    @ViewChild(VideoPlayerComponent) videoPlayer?: VideoPlayerComponent;
    public slug = input.required<string>();
    public part = input.required({ transform: toNumber(1) });
    public video!: Signal<Video | undefined>;
    public presentation!: Signal<{ language: string, slides: SlideItem[] } | undefined>;
    public embed = input(false, { transform: booleanAttribute });
    public subtitles!: Signal<Subtitle[] | undefined>;
    public activePart: Signal<VideoPart | undefined>;
    public isAuthenticated = this.userService.isAuthenticated;
    public user = this.userService.user;
    public isAdmin = this.userService.isAdmin;
    public isBookmarked = signal(false);
    public showMore = signal(true);
    public slidesOpened = signal(true);
    public faBookmarkSolid = faBookmarkSolid;
    public faBookmarkRegular = faBookmarkRegular;
    public faShare = faShare;
    public faPenToSquare = faPenToSquare;
    public AppRoutes = AppRoute;
    public AdminRoute = AdminRoute;
    public Licences = Licences;
    public faChevronDown = faChevronDown;
    public faChevronUp = faChevronUp;
    public faExternalLinkAlt = faExternalLinkAlt;
    public faLaptopCode = faLaptopCode;

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.slidesOpened.set(window.innerWidth > 768);
            window.addEventListener('beforeunload', () => this.storeLeftOff())
        }

        this.video = toSignal(
            toObservable(this.slug).pipe(
                filter(slug => !!slug),
                switchMap(slug => this.videoService.getVideoBySlug(slug)),
                tap(video => this.isBookmarked.set(video.bookmarked))
            ));

        this.activePart = computed(() => {
            const video = this.video();
            const part = video?.parts.find(part => part.part_number === this.part()) ?? video?.parts[0];

            if (video && !part) {

                if (this.isAdmin()) {
                    this.notificationService.error({ detail: 'Video without parts can not be displayed' });
                } else {
                    this.notificationService.error({ detail: 'Video could not be loaded, try again later' });
                }
            }

            if (part) {
                this.metaService.set({
                    title: video!.title,
                    description: video!.description,
                    image: part.thumbnail,
                    index: true
                })
            }

            return part;
        });

        this.presentation = computed(() => {
            const presentation = this.activePart()?.presentations?.[0];

            if (!presentation) {
                return undefined;
            }

            const slides = presentation.slides
                .flatMap(slide => slide.timestamps.map(timestamp => ({
                    id: slide.id,
                    title: slide.title,
                    image: slide.image,
                    timestamp
                })))
                .sort((a, b) => a.timestamp - b.timestamp);

            return { language: presentation.language, slides };
        });
    }

    public ngOnDestroy() {
        this.storeLeftOff();
    }

    @HostBinding('class.embed') get isEmbed() {
        return this.embed();
    }

    public selectSlide(slide: SlideItem) {
        this.videoPlayer?.changeSlide(slide)
    }

    public async toggleBookmark({ id }: Video) {
        if (!this.isAuthenticated()) return;

        if (this.isBookmarked()) {
            await this.bookmarksService.removeBookmark(id);
            this.isBookmarked.set(false);
        } else {
            await this.bookmarksService.addBookmark(id);
            this.isBookmarked.set(true);
        }
    }

    public get currentUrl() {
        return isPlatformBrowser(this.platformId) ? window.location.href : '';
    }

    get embedCode() {
        if (!isPlatformBrowser(this.platformId)) {
            return '';
        }

        let url = `${ window.origin }/embed/videos/${ this.slug() }`;

        if (this.part()) {
            url += `?part=${ this.part() }`;
        }

        return `<iframe src="${ url }" width="100%" frameborder="0" allowfullscreen style="aspect-ratio:16/9"></iframe>`;
    }

    public toggleSlides() {
        this.slidesOpened.set(!this.slidesOpened());
    }

    private storeLeftOff() {
        if (this.videoPlayer && this.videoPlayer.currentSecs > 5 && this.userService.isAuthenticated()) {
            this.videoService.addHistory(this.video()!.id, 0, this.videoPlayer.currentSecs);
        }
    }

    protected readonly faCalendarDays = faCalendarDays;
}
